// Lo que devuelve `GET /v1/metricas/tienda` de atlas-backend —
// ADR-2026-10-06-metricas-de-tienda-online, ADR-2026-10-06-metricas-de-tienda-en-el-backend.
//
// ⚠ Es el MISMO contrato que `atlas-backend/src/services/metricas/tipos.ts`: los dos archivos se
// mueven juntos. Todo lo que depende de un permiso que la tienda no dio (o de una lectura que
// falló) viene en `null` o ausente, y el motivo va en `faltan`: la pantalla dice "falta permiso"
// en vez de mostrar un cero que se leería como "no vendiste nada".

export type Plataforma = 'tiendanube' | 'mercadolibre';

/** Días hacia atrás. Cada número se compara contra los mismos días inmediatamente anteriores. */
export type Periodo = 7 | 30 | 90;

export interface Kpis {
  /** Suma de las órdenes pagas, sin canceladas. */
  ventas: number;
  /** Órdenes pagas. En Mercado Libre, un carrito con varios productos cuenta una vez (`pack_id`). */
  ordenes: number;
  unidades: number;
}

export interface PuntoDeVentas {
  /** `YYYY-MM-DD`, en hora de Argentina. */
  fecha: string;
  ventas: number;
  ordenes: number;
}

export interface ProductoVendido {
  nombre: string;
  unidades: number;
  ventas: number;
  /** Mercado Libre: las visitas de la publicación en el período. */
  visitas?: number;
}

export type MetricaId =
  | 'ventas'
  | 'carritos'
  | 'clientes'
  | 'stock'
  | 'margen'
  | 'envios'
  | 'neto'
  | 'visitas'
  | 'reputacion'
  | 'preguntas'
  | 'reclamos'
  | 'calidad'
  | 'publicidad';

export interface Faltante {
  metrica: MetricaId;
  /** `sin_permiso`: se arregla volviendo a conectar. `no_disponible`: Atlas todavía no la lee. `error`: reintentar. */
  motivo: 'sin_permiso' | 'no_disponible' | 'error';
  detalle: string;
}

export interface MetricasDePlataforma {
  plataforma: Plataforma;
  /** El nombre de la tienda o el apodo de la cuenta. */
  cuenta: string;
  moneda: string | null;
  /** `null` = no se pudieron leer las ventas (ver `faltan`). */
  actual: Kpis | null;
  anterior: Kpis | null;
  /** Un punto por día del período. */
  serie: PuntoDeVentas[] | null;
  topProductos: ProductoVendido[] | null;
  /** Hubo más órdenes que el tope por consulta: los números son de las más recientes. */
  truncado?: boolean;

  // ── Sólo Tienda Nube ──
  /** `/checkouts`: la API los guarda 30 días, así que se informan sobre esa ventana. */
  carritosAbandonados?: { cantidad: number; monto: number };
  clientes?: { nuevos: number; recurrentes: number };
  cupones?: Array<{ codigo: string; usos: number; descuento: number }>;
  /** `shipping_status` de las órdenes del período: si ya se despacharon o no. */
  envios?: { sinDespachar: number; despachadas: number };
  /** Las variantes de lo más vendido con su stock de hoy. `stock: null` = sin control de stock. */
  stock?: Array<{ producto: string; variante: string | null; vendidas: number; stock: number | null }>;
  /** Margen sobre lo vendido, sólo con los productos que tienen costo cargado en Tienda Nube. */
  margen?: { ventasConCosto: number; costoDeLoVendido: number; productosConCosto: number; productosVendidos: number };

  // ── Sólo Mercado Libre ──
  /** La comisión de Mercado Libre de los dos períodos. El envío que paga el vendedor todavía no se descuenta. */
  neto?: { actual: { comisiones: number }; anterior: { comisiones: number } };
  visitas?: { actual: number; anterior: number; serie: number[] };
  /** `seller_reputation`: Mercado Libre informa las tasas, NO los topes de cada color. */
  reputacion?: {
    /** 1 (rojo) a 5 (verde), como el termómetro de Mercado Libre. `null` = cuenta sin reputación todavía. */
    nivel: 1 | 2 | 3 | 4 | 5 | null;
    medalla: 'platinum' | 'gold' | 'silver' | null;
    /** Tasas en proporción (0.012 = 1,2%). */
    reclamos: number | null;
    demoras: number | null;
    cancelaciones: number | null;
    ventasCompletadas: number | null;
  };
  preguntasSinResponder?: number;
  reclamosAbiertos?: number;
  calidad?: Array<{ publicacion: string; puntaje: number; nivel: string }>;

  faltan: Faltante[];
}

export type EstadoDeTienda =
  | { estado: 'sin_conexion' }
  /** La plataforma no está habilitada para esta empresa (Mercado Libre, mientras sea sólo para testers). */
  | { estado: 'no_disponible' }
  | { estado: 'reconectar'; motivo: string }
  | { estado: 'ok'; metricas: MetricasDePlataforma }
  | { estado: 'error'; detalle: string };

export interface RespuestaMetricas {
  dias: Periodo;
  generadoAt: string;
  tiendanube: EstadoDeTienda;
  mercadolibre: EstadoDeTienda;
}

/** Ventas menos la comisión de Mercado Libre. `null` si no hay ventas o la plataforma no informa costos. */
export function ingresoNeto(d: MetricasDePlataforma, cual: 'actual' | 'anterior'): number | null {
  if (!d.neto || !d[cual]) return null;
  return d[cual]!.ventas - d.neto[cual].comisiones;
}

/** Lo que falta de una métrica, si falta. */
export function faltaDe(d: MetricasDePlataforma, metrica: MetricaId): Faltante | undefined {
  return d.faltan.find((f) => f.metrica === metrica);
}

export const NOMBRE_DE_PLATAFORMA: Record<Plataforma, string> = {
  tiendanube: 'Tienda Nube',
  mercadolibre: 'Mercado Libre',
};

/**
 * El color de cada plataforma en los gráficos. Validados juntos con el script de la
 * skill de dataviz (CVD ΔE 37, visión normal ΔE 45). El ámbar queda debajo de 3:1
 * contra el blanco: por eso los gráficos llevan leyenda, rótulo directo y vista de
 * tabla, y el texto nunca va de este color.
 */
export const COLOR_DE_PLATAFORMA: Record<Plataforma, string> = {
  tiendanube: '#1644EB', // hex-de-marca-ok: paleta categórica por plataforma, validada para CVD
  mercadolibre: '#D98E04',
};
